import { useQuery, useMutation } from '@tanstack/react-query'
import { api } from '@/lib/api'

// Query key factory
export const greetingKeys = {
  all: ['greetings'] as const,
  styles: () => [...greetingKeys.all, 'styles'] as const,
}

// Greeting styles cache
let stylesCache: string[] | null = null

/**
 * Hook to fetch available greeting styles
 */
export function useGreetingStyles(enabled = true) {
  return useQuery({
    queryKey: greetingKeys.styles(),
    queryFn: async (): Promise<string[]> => {
      if (stylesCache) return stylesCache

      const response = await api.get('/assistants/greetings/styles?_=1')
      stylesCache = response.data
      return response.data
    },
    enabled,
    staleTime: Infinity, // Styles rarely change
  })
}

export interface ReloadGreetingRequest {
  dynamic_enabled: boolean
  style?: string
  language?: string
  base_greeting?: string
  assistant_name?: string
  persona_name?: string
  additional_languages?: string[]
}

export interface ReloadGreetingResponse {
  greeting: string
  style: string
  language: string
  dynamic_enabled: boolean
  ivr_language_message?: string
}

/**
 * Hook to preview/reload a greeting
 */
export function useReloadGreeting() {
  return useMutation({
    mutationFn: async (
      request: ReloadGreetingRequest
    ): Promise<ReloadGreetingResponse> => {
      const response = await api.post('/assistants/greetings/reload', request)
      return response.data
    },
  })
}

export interface PlayAudioRequest {
  /**
   * Text to synthesize. When omitted, the backend picks a localized sample
   * greeting for `language` (used by the admin voice previews).
   */
  text?: string
  language: string
  type?: 'replacement' | 'phonetic'
  lexicon?: Record<string, { original: string; replacement: string; type: string }[]>
  id: number | string
  voice_id: number | string
}

/**
 * Play TTS audio for a greeting preview.
 * Returns the HTMLAudioElement so callers can pause/stop playback.
 *
 * By default playback starts before the promise resolves. Pass
 * `{ autoplay: false }` to get a not-yet-started element instead, so callers
 * with stale-request tokens can check the token *before* any sound starts and
 * discard the audio unplayed.
 */
export async function playGreetingAudio(
  params: PlayAudioRequest,
  opts?: { autoplay?: boolean }
): Promise<HTMLAudioElement> {
  const apiUrl = import.meta.env.VITE_API_URL
  const fullUrl = `${apiUrl}/assistants/play/audio`
  const token = localStorage.getItem('access_token')

  const response = await fetch(fullUrl, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(params),
  })

  if (!response.ok) {
    throw new Error(`Audio playback failed: ${response.status}`)
  }

  const audioBlob = await response.blob()
  const audioUrl = URL.createObjectURL(audioBlob)
  const audio = new Audio(audioUrl)
  if (opts?.autoplay !== false) {
    await audio.play()
  }
  return audio
}

export default useGreetingStyles
